import { useState, useEffect, useCallback } from 'react';

interface PriceData {
  price: number;
  change24h: number;
  loading: boolean;
  error: string | null;
}

const CMC_SHIDO_ID = '28802'; // SHIDO CoinMarketCap ID
const CACHE_KEY = 'shido_price_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedData {
  price: number;
  change24h: number;
  timestamp: number;
}

export const useSHIDOPrice = () => {
  const [priceData, setPriceData] = useState<PriceData>({
    price: 0,
    change24h: 0,
    loading: true,
    error: null,
  });

  const fetchPrice = useCallback(async () => {
    try {
      // Check cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedCache: CachedData = JSON.parse(cached);
        if (Date.now() - parsedCache.timestamp < CACHE_TTL) {
          setPriceData({
            price: parsedCache.price,
            change24h: parsedCache.change24h,
            loading: false,
            error: null,
          });
          return;
        }
      }

      // Fetch from CoinGecko (free, no API key needed)
      const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=shido-2&vs_currencies=usd&include_24hr_change=true'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch price');
      }

      const data = await response.json();
      
      if (data['shido-2']) {
        const price = data['shido-2'].usd || 0;
        const change24h = data['shido-2'].usd_24h_change || 0;

        // Cache the result
        const cacheData: CachedData = {
          price,
          change24h,
          timestamp: Date.now(),
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

        setPriceData({
          price,
          change24h,
          loading: false,
          error: null,
        });
      } else {
        throw new Error('Price data not found');
      }
    } catch (error) {
      console.error('Error fetching SHIDO price:', error);
      
      // Try to use cached data even if expired
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedCache: CachedData = JSON.parse(cached);
        setPriceData({
          price: parsedCache.price,
          change24h: parsedCache.change24h,
          loading: false,
          error: 'Using cached price',
        });
      } else {
        setPriceData({
          price: 0,
          change24h: 0,
          loading: false,
          error: 'Failed to fetch price',
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchPrice();

    // Refresh every 5 minutes
    const interval = setInterval(fetchPrice, CACHE_TTL);

    return () => clearInterval(interval);
  }, [fetchPrice]);

  const formatPrice = (price: number): string => {
    if (price >= 1) {
      return `$${price.toFixed(2)}`;
    } else if (price >= 0.01) {
      return `$${price.toFixed(4)}`;
    } else {
      return `$${price.toFixed(6)}`;
    }
  };

  const formatChange = (change: number): string => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  return {
    ...priceData,
    formattedPrice: formatPrice(priceData.price),
    formattedChange: formatChange(priceData.change24h),
    isPositive: priceData.change24h >= 0,
    refresh: fetchPrice,
  };
};
